import { useCallback, useEffect, useMemo, type ReactNode } from "react";
import { ProductUtil } from "@/utils/ProductUtil";
import { GameUtil } from "@/utils/GameUtil";
import { useCurrency } from "@/hooks/useCurrency";
import { useSellableGames } from "@/hooks/useBhapticsContents";
import { useBuyTactSuit } from "@/store/useBuyTactSuit";
import { ComponentId, RAIL_ERROR_HIGHLIGHT } from "@/constants";
import { cn } from "@/libs/utils";
import { GameKeySelect, type PlatformKeyStock } from "./GameKeySelect";
import { useGameResellsKeyCounts } from "@/hooks/useGameResell";
import { platformKeyStock } from "@/libs/cart/cart-game-stock";
import { useTrans } from "@/utils/useTrans";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/v2-components/ui/accordion";
import {
  RAIL_ACCORDION_ITEM,
  RAIL_ACCORDION_TRIGGER_WITH_CAPTION,
  RAIL_ACCORDION_CONTENT,
  RailSectionHeader,
} from "./rail-accordion";

/**
 * 게임 키 섹션의 아코디언 값. [slug].tsx 가 기본 열림 계산에 같은 값을 써야 한다.
 *
 * ⚠️ **인덱스가 아니라 `gameResell.id` 로 키를 잡는다.** 이 값은 "사용자가 접은 섹션"
 * 목록(`closedSections`)에 남는데, 인덱스로 잡으면 번들/제품을 바꿔 게임 목록이
 * 통째로 달라져도 같은 슬롯 번호가 재사용돼 **접은 적 없는 다른 게임의 섹션이
 * 닫힌 채로 뜬다**. 게임 키는 필수 선택이라 닫혀 있으면 놓치기 쉽다.
 */
export const gameKeyAccordionValue = (gameResellId: string) =>
  `game-key-${gameResellId}`;

/**
 * 선택된 제품(번들)에 포함된 재판매 가능 게임 목록.
 *
 * 아코디언 기본 열림 상태를 [slug].tsx 에서 계산해야 해서 훅으로 뺐다 —
 * 컴포넌트 안에서만 알고 있으면 부모가 섹션 개수를 몰라 기본 열림을 못 정한다.
 */
export function useProductGameKeys() {
  const sellableGames = useSellableGames();
  const currency = useCurrency();
  const selectedProduct = useBuyTactSuit((state) => state.product);

  const gameResellIds = selectedProduct
    ? ProductUtil.getProductGameResellIds(selectedProduct)
    : [];

  return GameUtil.getResellableGames({
    contents: sellableGames.data ?? [],
    gameResellIds,
    currency,
  });
}

/** 지금 재고 있음(`true`)이 아닌 플랫폼을 고른 슬롯의 인덱스 — 선택을 풀 대상(#6136). 빈 슬롯은 건너뛴다. */
export function staleGameKeySelections(
  selected: ({ id: string; platform: string } | undefined)[],
  stockOf: (gameResellId: string, platform: string) => PlatformKeyStock,
): number[] {
  return selected.flatMap((s, index) =>
    s?.id && s.platform && stockOf(s.id, s.platform) !== true ? [index] : [],
  );
}

/**
 * 번들에 든 게임 키의 **플랫폼별 재고** — `platformKeyStock`(장바구니 · 서버 결제 지점 · 목록 「재고 있음」과 같은 술어).
 *
 * 가격은 보지 않는다 — 번들 전용 게임 키(EXOSHOCK)는 가격 행이 없다(`docs/bundle-region-policy.md` 「예외」). 그래서
 * `sellableKeyPlatforms`(가격 ∧ 재고)가 아니라 재고 술어만 쓴다. 번들 노출 판정(`bundleGameResells`)도 같은 이유로 재고만 본다.
 * 조회 전 · 실패는 `null`(모름).
 */
export function useGameKeyPlatformStock(
  gameResellIds: string[],
): (gameResellId: string, platform: string) => PlatformKeyStock {
  // 매 렌더 새 배열이 와도 같은 id 집합이면 같은 참조를 넘긴다 — 조회 키 · 판정 함수가 렌더마다 바뀌지 않게
  const idsKey = gameResellIds.join(",");
  const ids = useMemo(() => (idsKey ? idsKey.split(",") : []), [idsKey]);
  const keyCounts = useGameResellsKeyCounts(ids);
  const byId = useMemo(
    () => new Map(keyCounts.data.map((r) => [r.id, r.data] as const)),
    [keyCounts.data],
  );
  return useCallback(
    (gameResellId: string, platform: string) =>
      platformKeyStock(byId.get(gameResellId), platform),
    [byId],
  );
}

/**
 * 게임 키 선택 섹션 (피그마 28769:63663 / 28769:63688).
 *
 * ⚠️ 레일에서 **Upgrade 다음**에 온다 — 어떤 번들을 고르느냐에 따라 받을 키가
 * 정해지므로 순서가 뒤집히면 안 된다. 그래서 `[slug].tsx` 의 `<Accordion>` 안,
 * upgrade 아이템 바로 뒤에 렌더된다(이전에는 아코디언 **앞**의 별도 블록이었다).
 *
 * 헤더(제목 + "Please select a game key")는 AccordionTrigger 가 갖는다 —
 * 접힌 상태에서도 미선택 경고가 보여야 하기 때문.
 */
export function ProductGameKeys({
  detailsLink,
}: {
  /** 목록 아래 「View … details ↓」(번들 전용 PDP — `src/components/tactsuit/components/buy-tactsuit/bundle-detail-rail.tsx`). 없으면 종전 그대로 */
  detailsLink?: ReactNode;
} = {}) {
  const { t } = useTrans(["shop"]);
  const games = useProductGameKeys();
  const stockOf = useGameKeyPlatformStock(games.map((g) => g.gameResell.id));

  const {
    selectedGameResells,
    toggleGameResell,
    initGameResells,
    showValidationErrors,
  } = useBuyTactSuit((state) => ({
    selectedGameResells: state.gameResells,
    toggleGameResell: state.toggleGameResell,
    initGameResells: state.initGameResells,
    showValidationErrors: state.showValidationErrors,
  }));

  // reset() 후 gameResells가 빈 배열이 되면 재초기화
  useEffect(() => {
    if (games.length > 0) {
      initGameResells(games.length);
    }
  }, [games.length, selectedGameResells.length, initGameResells]);

  // 고른 플랫폼이 **지금 재고 있음이 아니면**(품절 확인 · 다시 물어 모름) 선택을 푼다(#6136) — 담기 · 결제는 미선택 검증
  // (「Please select a game key」)이 막는다. 응답 전 깜빡임은 key-counts 의 `keepPreviousData` 가 막는다.
  // `toggleGameResell` 은 같은 값을 다시 넣으면 비운다.
  useEffect(() => {
    staleGameKeySelections(selectedGameResells, stockOf).forEach((index) =>
      toggleGameResell(selectedGameResells[index], index),
    );
  }, [selectedGameResells, stockOf, toggleGameResell]);

  if (!games.length) return null;

  return (
    <>
      {games.map((game, index) => {
        const title =
          games.length > 1
            ? `${t("Select Game Key")} ${index + 1}`
            : t("Select Game Key");
        const selection = selectedGameResells?.[index]
          ? [selectedGameResells[index]]
          : [];
        const selected = selection.some((s) => Boolean(s.id));

        return (
          <AccordionItem
            key={game.gameResell.id}
            value={gameKeyAccordionValue(game.gameResell.id)}
            // 검증 실패 시 이 id 로 스크롤한다(BuyTactsuitDetailPageTemplate)
            id={ComponentId.BUY_TACTSUIT_GAMEKEY_OPTION.replace(
              "{index}",
              index.toString(),
            )}
            className={cn(
              RAIL_ACCORDION_ITEM,
              // 에러 배경은 **섹션 전체**(헤더 + 보조줄 + 리스트) — 피그마 27471:53956.
              // 리스트에만 걸면 제목·경고문이 핑크 밖에 남는다.
              showValidationErrors && !selected && RAIL_ERROR_HIGHLIGHT,
            )}
          >
            <AccordionTrigger
              size="lg"
              className={RAIL_ACCORDION_TRIGGER_WITH_CAPTION}
            >
              <RailSectionHeader
                title={title}
                caption={!selected && t("Please select a game key")}
              />
            </AccordionTrigger>
            <AccordionContent
              className={cn(
                RAIL_ACCORDION_CONTENT,
                // 카드 → 링크 8(시안 그룹 gap 8 — 번들 전용 PDP 의 「View … details ↓」)
                !!detailsLink && "flex flex-col gap-2",
              )}
            >
              <GameKeySelect
                games={[game]}
                selectedGameResells={selection}
                stockOf={stockOf}
                onSelect={(option) => {
                  toggleGameResell(option, index);
                }}
              />
              {detailsLink}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </>
  );
}
